import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { login as loginApi } from '../services/api';
import { FiMail, FiLock } from 'react-icons/fi';
import LanguageSwitcher from '../components/LanguageSwitcher';
import FontSizeSwitcher from '../components/FontSizeSwitcher';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [blocked, setBlocked] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPending(false);
    setBlocked(null);
    setLoading(true);
    try {
      const res = await loginApi({ username: form.username, password: form.password });
      login(res.data.token, res.data.user);
      if (res.data.user.role === 'member') {
        navigate('/member/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'pending_approval') {
        setPending(true);
      } else if (data?.error === 'member_inactive') {
        setBlocked(data);
      } else {
        setError(data?.error || t('auth.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' };

  return (
    <div className="login-page">
      <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
        <FontSizeSwitcher />
        <LanguageSwitcher />
      </div>
      <div className="login-card">
        <div className="login-logo" style={{ marginBottom: 8 }}>
          <img src="/logo.png" alt="St. Michael Orthodox Church" style={{ width: 110, height: 110, objectFit: 'contain' }} />
        </div>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h1 style={{
            margin: 0,
            fontSize: 22,
            lineHeight: 1.25,
            fontWeight: 800,
            color: '#4f46e5',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>{t('login.churchName')}</h1>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, margin: '8px 0 6px' }}>
            <span style={{ flex: 1, maxWidth: 60, height: 1, background: '#d4a72c' }} />
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#b88a18',
              letterSpacing: '0.25em',
            }}>{t('login.country')}</span>
            <span style={{ flex: 1, maxWidth: 60, height: 1, background: '#d4a72c' }} />
          </div>
          <div style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>{t('login.systemTagline')}</div>
        </div>
        <p className="login-sub" style={{ marginBottom: 22 }}>{t('auth.signInToYourAccount')}</p>

        {pending && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '14px 16px', marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>⏳</div>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>{t('login.pendingTitle')}</div>
            <div style={{ fontSize: 13, color: '#a16207', marginTop: 4 }}>
              {t('login.pendingDesc')}
            </div>
          </div>
        )}

        {blocked && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 20 }}>🚫</span>
              <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: 14 }}>{t('login.blockedTitle')} — {blocked.status}</div>
            </div>
            <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.6 }}>
              {blocked.reason && blocked.reason !== 'No reason provided' && (
                <div><strong>{t('myProfile.reasonLabel')}:</strong> {blocked.reason}</div>
              )}
              {blocked.notes && <div style={{ marginTop: 4, fontStyle: 'italic' }}>{blocked.notes}</div>}
              {blocked.date && <div style={{ marginTop: 6, fontSize: 11, color: '#991b1b' }}>{t('login.effective')}: {blocked.date}</div>}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#991b1b' }}>
              {t('login.contactAdmin')}
            </div>
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ position: 'relative' }}>
            <label>{t('auth.username')}</label>
            <div style={{ position: 'relative' }}>
              <FiMail style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input style={inp} type="text" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} required autoFocus />
            </div>
          </div>
          <div className="form-group" style={{ position: 'relative' }}>
            <label>{t('auth.password')}</label>
            <div style={{ position: 'relative' }}>
              <FiLock style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input style={inp} type="password" placeholder="••••••••" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', padding: '11px', justifyContent: 'center', marginTop: 8 }}>
            {loading ? t('common.loading') : t('auth.login')}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#6b7280' }}>
          {t('auth.dontHaveAccount')}{' '}
          <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/register')}>
            {t('auth.createAccount')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
